// (Copyright) Confluent, Inc.
import * as React from 'react';
import styled from 'styled-components';

import { type Spacing } from '../../constants/spacings';
import { type Padding, paddingProp } from '../../utils/padding';
import { withDisableStyleOverrides } from '../../utils/disableOverride';

export type StackAlignment = 'start' | 'end' | 'center' | 'baseline' | 'stretch';
export type StackJustification =
  | 'start'
  | 'end'
  | 'center'
  | 'space-between'
  | 'space-around'
  | 'space-evenly';
export type StackTextAlignment = 'left' | 'center' | 'right' | 'inherit';
export type StackWrap = 'wrap' | 'nowrap' | 'wrap-reverse';

type Direction = 'row' | 'column';

const defaultAlignment = 'stretch';
const defaultJustification = 'start';
const defaultWrap = 'nowrap';

type StackProps = {
  ['aria-label']?: string;
  ['data-testid']?: string;
  alignment?: StackAlignment;
  children?: React.ReactNode;
  flexBasis?: string | number;
  flexGrow?: number;
  flexShrink?: number;
  gap?: Spacing;
  height?: string | number;
  justification?: StackJustification;
  maxHeight?: string | number;
  maxWidth?: string | number;
  minHeight?: string | number;
  minWidth?: string | number;
  padding?: Padding;
  role?: string;
  textAlignment?: StackTextAlignment;
  width?: string | number;
  wrap?: StackWrap;
};

/**
 * @deprecated Use `VStack` from `@confluent/axon` instead
 */
export const VStack = (props: StackProps) => <Stack {...props} direction="column" />;
/**
 * @deprecated Use `HStack` from `@confluent/axon` instead
 */
export const HStack = (props: StackProps) => <Stack {...props} direction="row" />;

const getAlignItems = (props: StackProps): string => {
  switch (props.alignment ?? defaultAlignment) {
    case 'start':
      return 'flex-start';
    case 'end':
      return 'flex-end';
    case 'center':
      return 'center';
    case 'baseline':
      return 'baseline';
    case 'stretch':
    default:
      return 'stretch';
  }
};

const getJustifyContent = (props: StackProps): string => {
  switch (props.justification ?? defaultJustification) {
    case 'end':
      return 'flex-end';
    case 'center':
      return 'center';
    case 'space-between':
      return 'space-between';
    case 'space-around':
      return 'space-around';
    case 'space-evenly':
      return 'space-evenly';
    case 'start':
    default:
      return 'flex-start';
  }
};

const Stack = withDisableStyleOverrides<
  StackProps & {
    direction: Direction;
  }
>(styled.div<any>`
  ${paddingProp(0)};

  display: flex;
  flex-direction: ${(props: any) => props.direction};
  align-items: ${getAlignItems};
  justify-content: ${getJustifyContent};
  flex-basis: ${(props: StackProps) =>
    typeof props.flexBasis === 'number' ? `${props.flexBasis}px` : props.flexBasis ?? 'auto'};
  flex-grow: ${(props: StackProps) => props.flexGrow ?? 0};
  flex-shrink: ${(props: StackProps) => props.flexShrink ?? 1};
  width: ${(props: StackProps) =>
    typeof props.width === 'number' ? `${props.width}px` : props.width ?? 'auto'};
  height: ${(props: StackProps) =>
    typeof props.height === 'number' ? `${props.height}px` : props.height ?? 'auto'};
  min-width: ${(props: StackProps) =>
    typeof props.minWidth === 'number' ? `${props.minWidth}px` : props.minWidth ?? 'none'};
  max-width: ${(props: StackProps) =>
    typeof props.maxWidth === 'number' ? `${props.maxWidth}px` : props.maxWidth ?? 'none'};
  min-height: ${(props: StackProps) =>
    typeof props.minHeight === 'number' ? `${props.minHeight}px` : props.minHeight ?? 'none'};
  max-height: ${(props: StackProps) =>
    typeof props.maxHeight === 'number' ? `${props.maxHeight}px` : props.maxHeight ?? 'none'};
  gap: ${(props: StackProps) => `${props.gap ?? 0}px`};

  text-align: ${(props: StackProps) => props.textAlignment ?? 'inherit'};
  flex-wrap: ${(props: StackProps) => props.wrap ?? defaultWrap};
`);
